/**
 * Incident Service
 * @description API service methods for incident-related operations
 */

import api from './api';
import { IncidentsResponse, IncidentFilters, IncidentDetail, ActivityLogsResponse, Incident } from '@/types/incident';

/**
 * Fetch all incidents with optional filters
 * @param filters - Optional filters: incident_id, site_url, origin, severity, search, state, start_date, end_date, include_archived, raised_from_alert, limit, offset
 */
export const getIncidents = async (
  filters?: IncidentFilters
): Promise<IncidentsResponse> => {
  try {
    // Build query parameters
    const params: Record<string, string | number | boolean | null> = {};
    
    if (filters?.incident_id) {
      params.incident_id = filters.incident_id;
    }
    if (filters?.site_url) {
      params.site_url = filters.site_url;
    }
    if (filters?.origin) {
      params.origin = filters.origin;
    }
    if (filters?.severity) {
      params.severity = filters.severity;
    }
    if (filters?.status_code) {
      params.status_code = filters.status_code;
    }
    if (filters?.search) {
      params.search = filters.search;
    }
    if (filters?.state) {
      params.state = filters.state;
    }
    if (filters?.start_date) {
      params.start_date = filters.start_date;
    }
    if (filters?.end_date) {
      params.end_date = filters.end_date;
    }
    if (filters?.include_archived !== undefined) {
      params.include_archived = filters.include_archived;
    }
    if (filters?.raised_from_alert !== undefined && filters.raised_from_alert !== null) {
      params.raised_from_alert = filters.raised_from_alert;
    }
    if (filters?.limit !== undefined) {
      params.limit = filters.limit;
    } else {
      params.limit = 100; // Default limit
    }
    if (filters?.offset !== undefined) {
      params.offset = filters.offset;
    } else {
      params.offset = 0; // Default offset
    }

    return await api.get<IncidentsResponse>('/incidents', params);
  } catch (error) {
    console.error('Error fetching incidents:', error);
    throw error;
  }
};

/**
 * Fetch a single incident by ID with full details
 */
export const getIncidentById = async (id: string): Promise<IncidentDetail> => {
  return api.get<IncidentDetail>(`/incidents/${id}`);
};

/**
 * Get incident activity log
 */
export const getIncidentActivities = async (id: string): Promise<ActivityLogsResponse> => {
  return api.get<ActivityLogsResponse>(`/incidents/${id}/activity`);
};

/**
 * Get all incident activities
 */
export const getAllIncidentActivities = async () => {
  return api.get('/activities/incidents');
};

/**
 * Change incident state response type
 */
export interface ChangeIncidentStateResponse {
  success: boolean;
  incident: Incident;
  state_history: Array<{
    id: number;
    incident_id: string;
    present_state: string;
    comment: string;
    updated_by: string;
    created_at: string;
  }>;
  message: string;
}

/**
 * Change incident state
 * @param incidentId - The incident ID (number)
 * @param stateData - Object containing state and comment
 * @returns Updated incident
 */
export const changeIncidentState = async (
  incidentId: string,
  stateData: {
    state: string;
    comment: string;
  }
): Promise<Incident> => {
  try {
    const response = await api.post<ChangeIncidentStateResponse>(`/incidents/change_state/${incidentId}`, stateData);
    return response?.incident as Incident;
  } catch (error) {
    console.error('Error changing incident state:', error);
    throw error;
  }
};

const incidentService = {
  getIncidents,
  getIncidentById,
  getIncidentActivities,
  getAllIncidentActivities,
  changeIncidentState,
};

export default incidentService;

